import { useState } from "react";
import type { EmploymentFormData } from "src/types/employment";
import DeleteEmploymentModal from "../DeleteEmploymentModal";
import { zeroDate, safeIntegerDivision } from "src/utils";
import { FormTableSummaryCard, WorkHistoryIcon } from "@porchpass/ui";

type EmploymentItemProps = {
  employment_detail: EmploymentFormData;
  index?: number;
  editHandler: () => void;
  closeHandler: () => void;
  deleteHandler: () => void;
};

const formatDateRange = (from: string, to?: string): string => {
  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      timeZone: "UTC",
    });

  return to && to !== zeroDate
    ? `From ${formatDate(from)} to ${formatDate(to)}`
    : `Since ${formatDate(from)}`;
};

export default ({
  employment_detail,
  index = 0,
  editHandler,
  closeHandler,
  deleteHandler,
}: EmploymentItemProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const isCurrent = Boolean(employment_detail.is_current);
  const isUnknown = !employment_detail.employer_name?.trim();
  const monthlyIncome = safeIntegerDivision(employment_detail.total_income, 12);

  const subtitles = [
    employment_detail.position_title,
    [
      employment_detail.street1,
      employment_detail.city,
      employment_detail.state,
      employment_detail.zip_code,
    ]
      .filter(Boolean)
      .join(", "),
    employment_detail.start_date
      ? formatDateRange(
          employment_detail.start_date,
          employment_detail.end_date,
        )
      : "",
  ].filter(Boolean) as string[];

  return (
    <>
      <FormTableSummaryCard
        index={index}
        title={employment_detail.employer_name || "Employer Unknown"}
        isUnknown={isUnknown}
        icon={
          <WorkHistoryIcon
            variant="bulk-rounded"
            size={16}
            className={isCurrent ? "text-primary" : "text-[#687083]"}
          />
        }
        iconTone={isCurrent ? "primary" : "default"}
        highlight={isCurrent ? "primary" : "none"}
        badges={
          isCurrent ? [{ label: "Current", variant: "primary" }] : undefined
        }
        subtitles={subtitles}
        amount={
          monthlyIncome
            ? {
                value: `$${monthlyIncome}`,
                suffix: "mo",
              }
            : undefined
        }
        onClick={editHandler}
        onEdit={editHandler}
        onDelete={() => setIsDeleteModalOpen(true)}
      />

      <DeleteEmploymentModal
        isOpen={isDeleteModalOpen}
        employment_id={employment_detail.id ?? ""}
        closeHandler={() => {
          setIsDeleteModalOpen(false);
          closeHandler();
        }}
        deleteHandler={() => {
          setIsDeleteModalOpen(false);
          deleteHandler();
        }}
      />
    </>
  );
};
